import React from 'react';
import { useApolloClient } from '@apollo/client';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons/faLock';
import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

import CTAToolTip from '@buyside/components/presentation/AccessControl/ctaTooltip';
import Link from '@buyside/components/presentation/Link/link';
import { useAccordionContext } from '../accordionContext';

import { noop } from '@buyside/util/lodashAlt';
import { accessIdeasUrl } from '@buyside/util/urlHelper';

import { IdeaCurrentUserQuery } from '@interfaces/graphql-typings';

const IdeaCurrentUser = require('buyside/graphql/queries/ideaShowPage/IdeaCurrentUser');

import styles from './accordionStyles.scss';

export interface AccordionTitleProps {
  children?: React.ReactNode;
  collapseIcon: IconDefinition;
  dataTag?: string;
  expandable: boolean;
  expandIcon: IconDefinition;
  iconStyles?: string;
  isLocked?: boolean;
  loading?: boolean;
  lockIcon?: IconDefinition;
  titleStyles?: string;
}

const AccordionTitle: React.FunctionComponent<AccordionTitleProps> = ({
  children,
  expandable,
  expandIcon,
  dataTag,
  iconStyles,
  collapseIcon,
  titleStyles,
  loading = false,
  lockIcon = faLock,
  isLocked = false
}): React.ReactElement => {
  const { isExpanded, toggleExpand } = useAccordionContext();

  const ideaId =
    window.location.pathname.match(/\/pro\/research\/ideas\/(?<ideaId>\d+)/)?.groups?.ideaId || '';

  const client = useApolloClient();
  const cachedCurrentUser = client.readQuery<IdeaCurrentUserQuery>({
    query: IdeaCurrentUser,
    variables: { ideaId }
  })?.currentUser;

  const showTooltip = ideaId && !cachedCurrentUser?.isRetail;

  return (
    <span
      data-testid="accordion-title"
      className={classNames(
        styles.title,
        {
          [styles.titleExpanded]: isExpanded,
          [styles.expandable]: expandable
        },
        titleStyles
      )}
      onClick={expandable && !isLocked ? toggleExpand : noop}
    >
      {children}
      {isLocked ? (
        <span className={classNames(styles.icon, iconStyles)}>
          {showTooltip ? (
            <CTAToolTip>
              <FontAwesomeIcon
                id="lock-icon"
                data-testid="lock-icon"
                icon={lockIcon}
                className={iconStyles}
              />
            </CTAToolTip>
          ) : (
            <Link to={accessIdeasUrl()}>
              <FontAwesomeIcon
                id="lock-icon"
                data-testid="lock-icon"
                icon={lockIcon}
                className={iconStyles}
              />
            </Link>
          )}
        </span>
      ) : (
        <span
          className={classNames(styles.icon, iconStyles)}
          data-heap={isExpanded ? `${dataTag}-close` : `${dataTag}-open`}
        >
          {loading ? (
            <FontAwesomeIcon id="spin-icon" icon={faSpinner} className={iconStyles} spin />
          ) : (
            <>
              {isExpanded ? (
                <FontAwesomeIcon
                  id="collapse-icon"
                  data-test-id="collapse-icon"
                  icon={collapseIcon}
                  className={iconStyles}
                />
              ) : (
                <FontAwesomeIcon
                  id="expand-icon"
                  data-testid="expand-icon"
                  icon={expandIcon}
                  className={iconStyles}
                />
              )}
            </>
          )}
        </span>
      )}
    </span>
  );
};

export default AccordionTitle;
